import * as Api from '@ViewModels';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { LevStandardQueryMeta, reactQueryClient } from '../../web/ReactQueryProvider';

const ACCOUNT_INTEGRATION_QUERY_KEY = 'account-integration';

export function useAccountIntegrationQuery<T extends Api.IConfigurableIntegration = Api.IConfigurableIntegration>({
	impersonationContext,
	type,
	enabled,
	meta,
}: {
	impersonationContext?: Api.IImpersonationContext;
	type: Api.ConfigurableIntegrationType;
	enabled?: boolean;
	meta?: LevStandardQueryMeta;
}) {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		queryFn: () => {
			return userSession.webServiceHelper.callAsync<T>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `account/integrations/${type}`,
				}),
				'GET'
			);
		},
		queryKey: [ACCOUNT_INTEGRATION_QUERY_KEY, type, impersonationContext],
		meta,
	});
}

export function useAccountIntegrationSuspenseQuery<
	T extends Api.IConfigurableIntegration = Api.IConfigurableIntegration,
>({
	impersonationContext,
	type,
	meta,
}: {
	impersonationContext?: Api.IImpersonationContext;
	type: Api.ConfigurableIntegrationType;
	enabled?: boolean;
	meta?: LevStandardQueryMeta;
}) {
	const userSession = useUserSession();
	return useSuspenseQuery({
		queryFn: () => {
			return userSession.webServiceHelper.callAsync<T>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `account/integrations/${type}`,
				}),
				'GET'
			);
		},
		queryKey: [ACCOUNT_INTEGRATION_QUERY_KEY, type, impersonationContext],
		meta,
		staleTime: 10 * 60 * 1000, //10 minutes
	});
}

export const invalidateAccountIntegrationQuery = ({
	impersonationContext,
	type,
}: {
	impersonationContext: Api.IImpersonationContext;
	type: Api.ConfigurableIntegrationType;
}) => reactQueryClient.invalidateQueries({ queryKey: [ACCOUNT_INTEGRATION_QUERY_KEY, type, impersonationContext] });
